import { useCallback, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
  formatDailyNoteTitle,
  formatWeeklyNoteTitle,
} from '../utils/noteDateUtils'
import {
  usePersonalNoteByDate,
  usePersonalNoteContent,
  usePersonalNoteContentMutations,
  usePersonalNoteMutations,
  usePersonalNoteNaturalKeyLookup,
} from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { useLocale } from '@/lib/i18n/LocaleProvider'

/**
 * Hook that finds a personal note for a specific date.
 * Does NOT auto-create — the note is created only on first content edit.
 * Returns a `createNoteIfNeeded` callback for the editor to call.
 */
export function usePersonalNoteForDate(
  noteType: 'daily' | 'weekly',
  noteDate: string | undefined,
) {
  const userId = useLoginStore((state) => state.userId)
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const { locale } = useLocale()
  const { note, isLoading: isNoteLoading } = usePersonalNoteByDate(noteType, noteDate)
  const { createPersonalNote } = usePersonalNoteMutations()
  const lookupByNaturalKey = usePersonalNoteNaturalKeyLookup()
  const { content, isLoading: isContentLoading } = usePersonalNoteContent(note?.id)
  const { createPersonalNoteContent, updatePersonalNoteContent } =
    usePersonalNoteContentMutations()
  const creatingRef = useRef(false)

  /**
   * Creates the note record on demand (called on first content edit).
   * Returns the new note ID so the editor can associate content with it.
   */
  const createNoteIfNeeded = useCallback(async (): Promise<string | null> => {
    // Already exists per the reactive hook
    if (note) return note.id

    if (!noteDate || !userId || !workspaceId || creatingRef.current) return null
    creatingRef.current = true

    try {
      // Re-check the DB directly — catches cross-device races where a
      // matching note was replicated in between the reactive hook's last
      // emission and this keystroke.
      const existing = await lookupByNaturalKey(
        workspaceId,
        userId,
        noteType,
        noteDate,
      )
      if (existing) return existing.id

      const now = Date.now()
      const title =
        noteType === 'daily'
          ? formatDailyNoteTitle(noteDate, locale)
          : formatWeeklyNoteTitle(noteDate)

      const noteId = uuidv4()

      await createPersonalNote({
        id: noteId,
        envId: workspaceId,
        userId,
        noteType,
        noteDate,
        title,
        isDeleted: false,
        createdOn: now,
        createdBy: userId,
        updatedOn: now,
        updatedBy: userId,
      })
      return noteId
    } finally {
      creatingRef.current = false
    }
  }, [
    note,
    noteDate,
    noteType,
    userId,
    workspaceId,
    createPersonalNote,
    lookupByNaturalKey,
    locale,
  ])

  return {
    note,
    content,
    isLoading: isNoteLoading || isContentLoading,
    createNoteIfNeeded,
    createPersonalNoteContent,
    updatePersonalNoteContent,
  }
}
